import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { ChatReplyDto } from "@/api/types";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Gallery } from "@/components/Gallery/Gallery";
import { AlertCircleIcon } from "@/components/Icons/AlertCircleIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { UserAvatar, UserAvatarLink } from "@/components/UserAvatar/UserAvatar";
import { VideoFilePreview } from "@/components/VideoFilePreview/VideoFilePreview";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { ContentBlock } from "@/design-system/ContentBlock";
import { IconButton } from "@/design-system/IconButton";
import { Spinner } from "@/design-system/Spinner";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useTranslation } from "@/translations";

import { EntityPreview } from "../EntityPreview/EntityPreview";

export type ChatMessageModel = Omit<ChatReplyDto, "image" | "videos"> & {
  image?: FormImage;
  videos: FormVideo[];
  deliveryStatus?: "sending" | "failed";
  onRetry?: () => void;
};

interface ChatMessageProps {
  message: ChatMessageModel;
  isLastOfMessageGroup: boolean;
}
export function ChatMessage({ message, isLastOfMessageGroup }: ChatMessageProps): React.ReactNode {
  const [isInViewport, setIsInViewport] = useState(false);

  const { i18n } = useTranslation();
  const sessionUser = useSessionUser();
  const postHog = usePostHog();

  const refContainer = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      setIsInViewport(true);
    }, []),
  });

  const { content, entity: attachedEntity } = useMemo(
    () => unfurlDeepLinkFromContent(message.text || ""),
    [message.text],
  );

  const isOwnMessage = message.author!.id === sessionUser.id;

  return (
    <div
      className={twJoin(
        "flex max-w-full gap-space-sm",
        isOwnMessage ? "self-end" : "self-start",
        !isLastOfMessageGroup && "pb-space-lg",
      )}
      data-testid="chat-message"
      ref={refContainer}
    >
      <aside className={twJoin("self-end", isOwnMessage && "order-last", isLastOfMessageGroup && `invisible`)}>
        {isOwnMessage ? <UserAvatar image={message.author!.avatar} /> : <UserAvatarLink user={message.author!} />}
      </aside>
      <article
        className={twJoin(
          "sentry-block relative min-w-0 rounded-lg px-space-md py-space-sm",
          isOwnMessage ? "bg-background-brand-light" : "bg-background-secondary",
        )}
      >
        <section className="flex w-full max-w-full flex-col gap-space-sm @md:max-w-measure-compact">
          {content && (
            <span className="max-w-full self-start text-body">
              <MultiLineContent>{content}</MultiLineContent>
            </span>
          )}
          {message.image && (
            <section className="max-w-full self-start" data-testid="chat-message-attachment-image">
              <Gallery images={[message.image]} />
            </section>
          )}
          {message.videos.length > 0 && (
            <section className="max-w-full self-start" data-testid="chat-message-attachment-video">
              <VideoFilePreview video={message.videos[0]} />
            </section>
          )}
          {attachedEntity && (
            <ContentBlock as="aside" width="flow" align="left" data-testid="attached-entity-preview">
              <EntityPreview
                entityId={attachedEntity.payload.entityId}
                entityType={attachedEntity.payload.entityType}
                otherIds={{
                  folderId: attachedEntity.payload.folderId,
                }}
                shouldLoad={isInViewport}
                onClick={() =>
                  postHog.capture("clicked_chat_entity_preview", {
                    entity_type: attachedEntity.payload.entityType,
                  })
                }
              />
            </ContentBlock>
          )}
          <footer className="flex items-center gap-space-xs self-end">
            <span className="text-overline text-text-secondary">{formatDate(i18n, "time", message.sentAt)}</span>
            {isOwnMessage && (
              <ChatMessageStatusIndicator
                status={message.deliveryStatus ?? (message.hasBeenSeenByAll ? "seen" : "sent")}
                onRetry={message.onRetry}
              />
            )}
          </footer>
        </section>
      </article>
    </div>
  );
}
type ChatMessageStatusIndicatorProps = {
  status: "sending" | "failed" | "sent" | "seen";
  onRetry?: () => void;
};
const ChatMessageStatusIndicator = ({ status, onRetry }: ChatMessageStatusIndicatorProps) => {
  const { t } = useTranslation();

  if (status === "sending") {
    return <Spinner size="xs" tone="secondary" />;
  }

  if (status === "failed") {
    return (
      <IconButton
        data-testid="retry-chat-message"
        title={t("component.chat-message.retry")}
        onClick={onRetry}
        icon={<AlertCircleIcon size={16} />}
        variant="subtle"
        tone="error"
        size="xs"
        isCircular
      />
    );
  }

  const statusLabel =
    status === "seen" ? t("component.chat-message.status.seen") : t("component.chat-message.status.sent");

  return (
    <span
      role="img"
      aria-label={statusLabel}
      className={twJoin("flex", status === "seen" && "text-text-brand", status === "sent" && `text-text-secondary`)}
    >
      <span className="-mr-2.5 flex">
        <CheckIcon />
      </span>
      <CheckIcon />
    </span>
  );
};
